import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, RefreshCw, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { Masked } from "@/components/Masked";
import { MobileRow } from "@/components/MobileRow";

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const dur = (s) => {
  if (!s && s !== 0) return "—";
  const n = Math.floor(s);
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${n % 60}s`;
  return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
};

const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500";
const td = "px-3 py-2 font-mono text-xs text-zinc-300 border-t border-zinc-900";

export const EaHealth = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const diagnose = async () => {
    setDiagBusy(true);
    try {
      const calls = [];
      for (let i = 0; i < 3; i++) {
        const { data } = await api.get("/admin/ea-diagnose");
        calls.push(data);
        await new Promise((r) => setTimeout(r, 400));
      }
      const boots = [...new Set(calls.map((c) => c.boot_id))];
      const problems = [...new Set(calls.flatMap((c) => c.problems))];
      setDiag({
        boots,
        multiInstance: boots.length > 1,
        problems,
        slots: calls[calls.length - 1].slots,
      });
    } catch {
      setErr("Diagnostics failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ea-health");
      setData(data);
      setErr("");
    } catch {
      setErr("Could not load EA health");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const rows = data?.rows || [];

  return (
    <div
      className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 sm:p-5 space-y-4"
      data-testid="ea-health-card"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Activity className="h-4 w-4 text-blue-400" />
        <h2 className="text-base md:text-lg text-zinc-100">EA connections</h2>
        {data && (
          <span
            className="font-mono text-[11px] text-zinc-500"
            data-testid="ea-health-summary"
          >
            {data.online}/{data.total} online
            {data.drops_today > 0 && (
              <span className="text-amber-500"> · {data.drops_today} drops today</span>
            )}
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={diagnose}
          disabled={diagBusy}
          data-testid="ea-diagnose-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-zinc-800 hover:border-blue-600 text-[10px] uppercase tracking-[0.1em] font-semibold text-zinc-400 hover:text-blue-400 rounded-sm disabled:opacity-50 transition-colors duration-200"
          title="Why does the EA say connected while a trader sees offline?"
        >
          <Stethoscope className="h-3 w-3" />
          {diagBusy ? "checking…" : "Diagnose"}
        </button>
        <button
          onClick={load}
          data-testid="ea-health-refresh"
          className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors duration-200"
          title="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {err && <p className="font-mono text-xs text-red-400">{err}</p>}

      {diag && (
        <div
          className="border border-zinc-800 bg-black/50 rounded-sm p-3 space-y-2"
          data-testid="ea-diagnose-result"
        >
          <p className="font-mono text-[11px] text-zinc-400">
            backend instances seen: {diag.boots.join(", ")}
          </p>
          {diag.multiInstance ? (
            <p className="font-mono text-[11px] text-zinc-400" data-testid="ea-diagnose-verdict">
              {diag.boots.length} backend instances are serving this app. EA connections,
              snapshots and commands are shared through MongoDB, so a trader is seen as
              connected no matter which instance serves their page.
            </p>
          ) : (
            <p className="font-mono text-[11px] text-emerald-400" data-testid="ea-diagnose-verdict">
              Single backend instance — EA state is shared with every page request.
            </p>
          )}
          {diag.problems.length > 0 ? (
            diag.problems.map((p) => (
              <p key={p} className="font-mono text-[11px] text-amber-500">
                · {p}
              </p>
            ))
          ) : (
            <p className="font-mono text-[11px] text-zinc-500">No slot mismatches found.</p>
          )}
          <div className="pt-1">
            {diag.slots.map((s) => (
              <p key={s.slot_id} className="font-mono text-[10px] text-zinc-600">
                {s.slot_id} ·{" "}
                {s.assigned_to ? (
                  <Masked value={s.assigned_to} kind="email"
                          testid={`ea-health-assigned-${s.slot_id}`} />
                ) : (
                  "unassigned"
                )}{" "}
                · in this instance:{" "}
                {s.live_on_this_instance ? "yes" : "no"} · shared state:{" "}
                {s.shared_state_online ? "online" : "offline"} · heartbeat:{" "}
                {s.heartbeat_age_s === null || s.heartbeat_age_s === undefined
                  ? "never"
                  : `${s.heartbeat_age_s}s ago`}
              </p>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="font-mono text-xs text-zinc-600">
          No traders have a slot assigned yet.
        </p>
      ) : (
        <>
        <div className="md:hidden divide-y divide-zinc-800 -mx-4">
          {rows.map((r) => (
            <MobileRow
              key={r.slot_id}
              testid={`ea-health-card-${r.port}`}
              title={
                <>
                  <Masked value={r.email || r.user_id} kind="email"
                          testid={`ea-health-card-trader-${r.slot_id}`}
                          className="text-xs text-zinc-100" />
                  <span className="block font-mono text-[10px] text-zinc-600">
                    {r.slot_id} · port {r.port}
                  </span>
                </>
              }
              badge={
                <span className="inline-flex items-center gap-1.5 shrink-0">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      r.online ? "bg-emerald-400" : "bg-zinc-700"
                    }`}
                  />
                  <span
                    className={`font-mono text-[10px] ${
                      r.online ? "text-emerald-400" : "text-zinc-500"
                    }`}
                  >
                    {r.online ? "connected" : "offline"}
                  </span>
                </span>
              }
              rows={[
                ["Uptime", r.online ? dur(r.uptime_s) : `down ${ago(r.last_disconnect_at)}`],
                ["Last heartbeat", r.online ? `${dur(r.idle_s)} ago` : "—"],
                ["Keep-warm", r.last_keepwarm_at ? ago(r.last_keepwarm_at) : "never"],
                ["Drops today", r.drops_today],
                ["Pushes", r.online ? r.snapshots : "—"],
                ["Instruments", r.instruments || "—"],
                ["Terminal", r.terminal?.account
                  ? `#${r.terminal.account} · build ${r.terminal.build || "?"} · EA ${
                      r.terminal.ea_version || "≤1.12"}`
                  : "—"],
                r.last_reason ? ["Last reason", r.last_reason] : null,
              ]}
            />
          ))}
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0 hidden md:block">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr>
                <th className={th}>Trader</th>
                <th className={th}>Status</th>
                <th className={th}>Uptime</th>
                <th className={th}>Last heartbeat</th>
                <th className={th}>Keep-warm</th>
                <th className={th}>Drops today</th>
                <th className={th}>Terminal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slot_id} data-testid={`ea-health-row-${r.port}`}>
                  <td className={td}>
                    <Masked value={r.email || r.user_id} kind="email"
                             testid={`ea-health-trader-${r.slot_id || r.user_id}`}
                             className="text-zinc-200" />
                    <div className="text-[10px] text-zinc-600">
                      {r.slot_id} · port {r.port}
                    </div>
                  </td>
                  <td className={td}>
                    <span
                      className="inline-flex items-center gap-1.5"
                      data-testid={`ea-health-status-${r.port}`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          r.online ? "bg-emerald-400" : "bg-zinc-700"
                        }`}
                      />
                      <span className={r.online ? "text-emerald-400" : "text-zinc-500"}>
                        {r.online ? "connected" : "offline"}
                      </span>
                    </span>
                  </td>
                  <td className={td}>
                    {r.online ? dur(r.uptime_s) : (
                      <span className="text-zinc-600" data-testid={`ea-health-drop-${r.port}`}>
                        down {ago(r.last_disconnect_at)}
                        {r.last_reason ? (
                          <span className="block text-[10px] text-amber-600/80">
                            {r.last_reason}
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    {r.online ? (
                      <span
                        className={
                          r.idle_s > 60 ? "text-amber-500" : "text-zinc-400"
                        }
                      >
                        {dur(r.idle_s)} ago · {r.snapshots} pushes
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                  <td className={td} data-testid={`ea-health-keepwarm-${r.port}`}>
                    {r.last_keepwarm_at ? (
                      <span
                        className={
                          (Date.now() - new Date(r.last_keepwarm_at).getTime()) / 1000 > 120
                            ? "text-amber-500"
                            : "text-emerald-400"
                        }
                      >
                        {ago(r.last_keepwarm_at)}
                      </span>
                    ) : (
                      <span className="text-zinc-600" title="EA v2.11 or newer pings every 45s">
                        never
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    {r.drops_today > 2 ? (
                      <span className="inline-flex items-center gap-1 text-amber-500">
                        <AlertTriangle className="h-3 w-3" /> {r.drops_today}
                      </span>
                    ) : (
                      <span className={r.drops_today ? "text-zinc-400" : "text-zinc-600"}>
                        {r.drops_today}
                      </span>
                    )}
                  </td>
                  <td className={`${td} text-zinc-500`} data-testid={`ea-health-terminal-${r.port}`}>
                    {r.terminal?.account
                      ? `#${r.terminal.account} · ${r.terminal.company || ""} · build ${
                          r.terminal.build || "?"
                        } · EA ${r.terminal.ea_version || "≤1.12"}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <p className="text-[10px] text-zinc-600 font-mono">
        Refreshes every 10s. A heartbeat older than a minute or repeated drops usually means
        the VPS lost its connection. Keep-warm is the EA's 45s HTTP ping (v2.11+) that stops the
        backend pod being scaled away under a live socket — older than 2 minutes and it is not
        running.
      </p>
    </div>
  );
};
